import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlass,
  faXmark,
  faClock,
  faCalendarAlt,
  faArrowRight,
  faCircleExclamation,
  faInbox,
} from '@fortawesome/free-solid-svg-icons';
import './Dashboard.css';

const API = process.env.REACT_APP_API_BASE_URL;

const toDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
};

const fmtDate = (d) => (d ? d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtTime = (d) => (d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

// Badge colour for the class state / report columns (same rules as before)
const completionClass = (s = '') => {
  const v = s.toLowerCase();
  if (v.includes('completed')) return 'is-green';
  if (v.includes('scheduled')) return 'is-blue';
  return 'is-amber';
};

const reportClass = (s = '') => {
  const v = s.toLowerCase();
  if (v.includes('full pay') || v.includes('enrolled')) return 'is-green';
  if (v.includes('half pay') || v.includes('postponed')) return 'is-amber';
  if (v.includes('pending')) return 'is-blue';
  if (v.includes('awaiting')) return 'is-grey';
  return 'is-red';
};

const TeacherDashboard = ({ userData }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [reload, setReload] = useState(0);

  const teacherId = userData?.id;
  const isCurrentMonth = month === now.getMonth() && year === now.getFullYear();
  const nextIsFuture = new Date(year, month + 1, 1) > now;

  // ---------- Data (same endpoint), ignores late answers when the month changes fast ----------
  useEffect(() => {
    if (!teacherId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    axios
      .get(`${API}/api/teacher/dashboard/${teacherId}/${month + 1}/${year}`)
      .then((res) => {
        if (!cancelled) setData(res.data || {});
      })
      .catch((err) => {
        console.error('API Error:', err);
        if (!cancelled) setError('We couldn’t load this month.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [teacherId, month, year, reload]);

  const changeMonth = (step) => {
    const d = new Date(year, month + step, 1);
    if (step > 0 && d > now) return;
    setMonth(d.getMonth());
    setYear(d.getFullYear());
  };

  const goToCurrent = () => {
    setMonth(now.getMonth());
    setYear(now.getFullYear());
  };

  // ---------- Derived numbers ----------
  const sessions = useMemo(
    () =>
      (data?.allSessions || [])
        .map((s) => ({ ...s, date: toDate(s.ClassDateTime) }))
        .sort((a, b) => (a.date && b.date ? b.date - a.date : 0)),
    [data]
  );

  const paid = {
    full: data?.fullPayCount || 0,
    half: data?.halfPayCount || 0,
    none: data?.noPayCount || 0,
  };

  const trial = {
    enrolled: data?.enrolledStudents || 0,
    pending: data?.pendingStudents || 0,
    other:
      (data?.registrationPostponedStudents || 0) +
      (data?.notInterestedCount || 0) +
      (data?.awaiting || 0) +
      (data?.caseClosedStudents || 0),
  };

  const trialCount = sessions.filter((s) => s.studenttype === 'demo').length;
  const conversion = trialCount > 0 ? Math.min(100, Math.round((trial.enrolled / trialCount) * 100)) : 0;

  const upNext = useMemo(() => {
    if (!isCurrentMonth) return null;
    const t = Date.now();
    return [...sessions].filter((s) => s.date && s.date.getTime() > t).sort((a, b) => a.date - b.date)[0] || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, isCurrentMonth]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sessions.filter(
      (s) => (type === 'all' || s.studenttype === type) && (!q || (s.StudentFullName || '').toLowerCase().includes(q))
    );
  }, [sessions, search, type]);

  const monthLabel = new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const firstName = userData?.firstName || userData?.fullName?.split(' ')[0] || 'Teacher';

  return (
    <div className="rmtd">
      <div className="rmtd-inner">
        {/* ---------- Header + month ---------- */}
        <header className="rmtd-head">
          <div>
            <span className="rmtd-eyebrow">Teaching dashboard</span>
            <h1 className="rmtd-title">
              Hello, <span>{firstName}</span>
            </h1>
          </div>

          <div className="rmtd-month" role="group" aria-label="Choose month">
            <button type="button" className="rmtd-month-btn" onClick={() => changeMonth(-1)} aria-label="Previous month" disabled={loading}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="rmtd-month-label" aria-live="polite">
              {monthLabel}
            </span>
            <button
              type="button"
              className="rmtd-month-btn"
              onClick={() => changeMonth(1)}
              aria-label="Next month"
              disabled={nextIsFuture || loading}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            {!isCurrentMonth && (
              <button type="button" className="rmtd-month-now" onClick={goToCurrent}>
                This month
              </button>
            )}
          </div>
        </header>

        {error ? (
          <div className="rmtd-state">
            <FontAwesomeIcon icon={faCircleExclamation} className="rmtd-state-icon" />
            <p>{error} Please try again.</p>
            <button type="button" className="rmtd-btn" onClick={() => setReload((n) => n + 1)}>
              Try again
            </button>
          </div>
        ) : loading && !data ? (
          <>
            <div className="rmtd-stats">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rmtd-skel rmtd-skel--stat" />
              ))}
            </div>
            <div className="rmtd-skel rmtd-skel--table" />
          </>
        ) : (
          <div className={loading ? 'rmtd-dim' : ''} aria-busy={loading}>
            {/* ---------- Up next ---------- */}
            {upNext && (
              <section className="rmtd-next" aria-label="Your next class">
                <div className="rmtd-next-when">
                  <span>{upNext.date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                  <strong>{upNext.date.getDate()}</strong>
                  <span>{upNext.date.toLocaleDateString('en-US', { month: 'short' })}</span>
                </div>
                <div className="rmtd-next-body">
                  <span className="rmtd-next-label">Up next</span>
                  <h2>{upNext.StudentFullName || 'Student'}</h2>
                  <p>
                    <span className={`rmtd-type is-${upNext.studenttype}`}>{upNext.studenttype === 'paid' ? 'Paid' : 'Trial'}</span>
                    <FontAwesomeIcon icon={faClock} /> {fmtTime(upNext.date)}
                  </p>
                </div>
                <Link to="/teacher/MyClasses" className="rmtd-btn">
                  My classes <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              </section>
            )}

            {/* ---------- Stats ---------- */}
            <div className="rmtd-stats">
              <section className="rmtd-stat" aria-labelledby="rmtd-paid">
                <h2 id="rmtd-paid" className="rmtd-stat-title">
                  Paid classes
                </h2>
                <div className="rmtd-chips">
                  <div className="rmtd-chip is-green">
                    <strong>{paid.full}</strong>
                    <span>Full pay</span>
                  </div>
                  <div className="rmtd-chip is-amber">
                    <strong>{paid.half}</strong>
                    <span>Half pay</span>
                  </div>
                  <div className="rmtd-chip is-red">
                    <strong>{paid.none}</strong>
                    <span>No pay</span>
                  </div>
                </div>
              </section>

              <section className="rmtd-stat" aria-labelledby="rmtd-trial">
                <h2 id="rmtd-trial" className="rmtd-stat-title">
                  Trial classes
                </h2>
                <div className="rmtd-chips">
                  <div className="rmtd-chip is-green">
                    <strong>{trial.enrolled}</strong>
                    <span>Enrolled</span>
                  </div>
                  <div className="rmtd-chip is-blue">
                    <strong>{trial.pending}</strong>
                    <span>Pending</span>
                  </div>
                  <div className="rmtd-chip is-grey">
                    <strong>{trial.other}</strong>
                    <span>Other</span>
                  </div>
                </div>
              </section>

              <section className="rmtd-stat" aria-labelledby="rmtd-conv">
                <h2 id="rmtd-conv" className="rmtd-stat-title">
                  Trial → enrolled
                </h2>
                <div className="rmtd-conv">
                  <strong>{conversion}%</strong>
                  <span>
                    {trial.enrolled} of {trialCount} trial {trialCount === 1 ? 'class' : 'classes'}
                  </span>
                </div>
                <div className="rmtd-bar" aria-hidden="true">
                  <span style={{ width: `${conversion}%` }} />
                </div>
              </section>
            </div>

            {/* ---------- Sessions ---------- */}
            <section className="rmtd-sessions" aria-labelledby="rmtd-sessions-title">
              <div className="rmtd-sessions-head">
                <h2 id="rmtd-sessions-title" className="rmtd-section-title">
                  Classes in {new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long' })}{' '}
                  <span className="rmtd-count">{filtered.length}</span>
                </h2>

                <div className="rmtd-tools">
                  <div className="rmtd-search">
                    <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
                    <input
                      type="search"
                      placeholder="Search a student"
                      aria-label="Search a student"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                      <button type="button" onClick={() => setSearch('')} aria-label="Clear search">
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    )}
                  </div>

                  <div className="rmtd-seg" role="radiogroup" aria-label="Class type">
                    {[
                      ['all', 'All'],
                      ['paid', 'Paid'],
                      ['demo', 'Trial'],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={type === value}
                        className={type === value ? 'is-on' : ''}
                        onClick={() => setType(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {filtered.length === 0 ? (
                <div className="rmtd-empty">
                  <FontAwesomeIcon icon={faInbox} />
                  <p>{sessions.length ? 'No classes match your search.' : `No classes in ${monthLabel}.`}</p>
                </div>
              ) : (
                <div className="rmtd-table-wrap">
                  <table className="rmtd-table">
                    <thead>
                      <tr>
                        <th scope="col">Student</th>
                        <th scope="col">Type</th>
                        <th scope="col">Date</th>
                        <th scope="col">Class</th>
                        <th scope="col">Report</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((s, i) => (
                        <tr key={`${s.ClassDateTime}-${s.StudentFullName}-${i}`}>
                          <td className="rmtd-cell-student">{s.StudentFullName || '—'}</td>
                          <td>
                            <span className={`rmtd-type is-${s.studenttype}`}>{s.studenttype === 'paid' ? 'Paid' : 'Trial'}</span>
                          </td>
                          <td className="rmtd-cell-date">
                            <FontAwesomeIcon icon={faCalendarAlt} className="rmtd-cell-icon" />
                            <span>{fmtDate(s.date)}</span>
                            <small>{fmtTime(s.date)}</small>
                          </td>
                          <td>
                            {s.ClassCompletionStatus && (
                              <span className={`rmtd-badge ${completionClass(s.ClassCompletionStatus)}`}>{s.ClassCompletionStatus}</span>
                            )}
                          </td>
                          <td>
                            {s.ClassStatus && <span className={`rmtd-badge ${reportClass(s.ClassStatus)}`}>{s.ClassStatus}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherDashboard;